import { Card, Select } from "@rothoblaas/design-system";
import classNames from "classnames";
import { CSSProperties, ReactNode, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import {
  FINISH_ALUMINIUM,
  FINISH_EVO,
  LATERAL_RESISTANCE_CONFIG,
  UPLIFT_RESISTANCE_CONFIG,
  type FastenerLight,
  type Finish,
  type LockLight,
  type LockStop,
  type OperationMode,
} from "../../../../../http/lock_calculator/types";
import { FastenerCompatibilityFilter, useFasteners } from "../../../../../http/lock_calculator/useFasteners";
import { LockGeometryFilter, useLocks } from "../../../../../http/lock_calculator/useLocks";
import { LockstopCompatibilityFilter, useLockStops } from "../../../../../http/lock_calculator/useLockStops";
import { joistTopAlignmentForPayload, joistTopOffsetForPayload, userTopDistance } from "../../data/calculationAdapter";
import { useConnectorSelection } from "../../hooks/useConnectorSelection";
import { LC_IMG } from "../../constants";
import styles from "../../LockCalculator.module.css";
import { isConnectorsStepComplete } from "../../model/connectorsCompleteness";
import type { LockCalculatorFormValues } from "../../model/form";
import { applyCascade } from "../notices/applyCascade";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";
import { noticeText } from "../notices/noticeText";
import { actionTargetKey } from "../notices/noticeTargets";
import { useNoticeTarget, useNotices } from "../notices/NoticesContext";

const CONNECTOR_COUNT_OPTIONS = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
];
const connectorCountImages: Record<number, string> = {
  1: `${LC_IMG}/lock-t-connection-1.png`,
  2: `${LC_IMG}/lock-t-connection-2.png`,
};
const lockLabel = (lock: LockLight) => lock.description;
const finishBadgeStyle = (finish: Finish | null): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: "64px",
  height: "40px",
  fontSize: "0.95rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  borderRadius: "6px",
  border: "2px solid",
  transition: "border-color 0.15s, color 0.15s, background 0.15s",
  ...(finish === FINISH_ALUMINIUM
    ? { background: "#e8e8e8", borderColor: "#b0b0b0", color: "#555" }
    : finish === FINISH_EVO
      ? { background: "#2a2a2a", borderColor: "#555", color: "#ccc" }
      : { background: "#f5f5f5", borderColor: "#ddd", color: "#bbb" }),
});

type Props = {
  readOnly: boolean;
  isAdmin: boolean;
  onOpenHelp: (text: string, image?: string) => void;
  onTriggerOperation: (operationMode: OperationMode) => Promise<void>;
  /** After a generation returns: show what it produced in the results. */
  onGenerated: (operationMode: OperationMode) => void;
  isTriggeringOperation: boolean;
  stepper: ReactNode;
};

export const ConnectorsStep = ({ readOnly, isAdmin, onOpenHelp, onTriggerOperation, onGenerated, isTriggeringOperation, stepper }: Props) => {
  const { translation: t } = useI18n();
  const form = useFormContext<LockCalculatorFormValues>();
  const generate = async (operationMode: OperationMode) => {
    await onTriggerOperation(operationMode);
    onGenerated(operationMode);
  };
  const values = useWatch({ control: form.control });
  const firstLockItemId = values?.connectorPairs?.[0]?.lockItemId ?? null;
  const secondLockItemId = values?.connectorPairCount === 2 ? (values?.connectorPairs?.[1]?.lockItemId ?? null) : null;
  // Same rule filterLockstopsByMapping (backend) applies when generating
  // configurations — sent here too so the lockstop select only offers
  // lockstops mapped to the chosen lock(s), without duplicating that rule
  // on the frontend.
  const lockstopCompatibility = useMemo<LockstopCompatibilityFilter>(
    () => ({ lockItemId: firstLockItemId, secondLockItemId }),
    [firstLockItemId, secondLockItemId],
  );
  const lockStopsAborter = useRef(new AbortController());
  const lockStopsQuery = useLockStops(
    { limit: 200, page: 1, q: "" },
    lockStopsAborter.current.signal,
    isAdmin,
    lockstopCompatibility,
  );
  const lockStops = lockStopsQuery.data?.items ?? [];
  const isLoadingLockStops = lockStopsQuery.isLoading;
  // Same geometry inputs filterCompatibleLocks (backend) uses to narrow
  // configuration-generation candidates — sent here too so both connector
  // selects only offer locks compatible with the structure the user already
  // entered, without duplicating that compatibility rule on the frontend.
  // Also includes an already-chosen header/joist fastener, so picking a
  // fastener first narrows the lock select too (the reverse of lock -> fastener).
  const lockGeometryFilter = useMemo<LockGeometryFilter>(
    () => ({
      headerOrientation: values?.headerMember?.orientation,
      headerB: values?.headerMember?.width,
      headerH: values?.headerMember?.height,
      joistB: values?.joistMember?.width,
      joistH: values?.joistMember?.height,
      connectorTopDistance: values?.joistMember ? userTopDistance(values as LockCalculatorFormValues) : null,
      joistTopAlignment: values?.joistMember ? joistTopAlignmentForPayload(values as LockCalculatorFormValues) : null,
      joistTopOffset: values?.joistMember ? joistTopOffsetForPayload(values as LockCalculatorFormValues) : null,
      joistMaterialGradeId: values?.joistMember?.materialGradeId,
      headerFastenerItemId: values?.fasteners?.headerItemId,
      joistFastenerItemId: values?.fasteners?.joistItemId,
    }),
    [
      values?.headerMember?.orientation,
      values?.headerMember?.width,
      values?.headerMember?.height,
      values?.joistMember?.width,
      values?.joistMember?.height,
      values?.joistMember?.connectorTopDistance,
      values?.joistMember?.connectorTopDistanceDefault,
      values?.joistMember?.topAlignment,
      values?.joistMember?.topOffsetFromHeader,
      values?.joistMember?.topOffsetDefault,
      values?.joistMember?.materialGradeId,
      values?.fasteners?.headerItemId,
      values?.fasteners?.joistItemId,
    ],
  );
  const connectorOneAborter = useRef(new AbortController());
  const connectorOneLocksQuery = useLocks(
    { limit: 200, page: 1, q: "" },
    connectorOneAborter.current.signal,
    isAdmin,
    null,
    lockGeometryFilter,
  );
  const locksForConnectorOne = connectorOneLocksQuery.data?.items ?? [];
  const isLoadingLocksForConnectorOne = connectorOneLocksQuery.isLoading;
  const connectorTwoAborter = useRef(new AbortController());
  const connectorTwoLocksQuery = useLocks(
    { limit: 200, page: 1, q: "" },
    connectorTwoAborter.current.signal,
    isAdmin,
    firstLockItemId,
    lockGeometryFilter,
  );
  const locksForConnectorTwo = connectorTwoLocksQuery.data?.items ?? [];
  const isLoadingLocksForConnectorTwo = connectorTwoLocksQuery.isLoading;

  // Same rules isFastenerCompatibleWithLock/Header/Joist/Inclined (backend)
  // apply when generating configurations — sent here too so the fastener
  // selects only offer fasteners compatible with the lock(s) and geometry
  // already entered, without duplicating those rules on the frontend.
  // Both pairs share one finish (useConnectorSelection keeps them in sync).
  const structureFinish = values?.connectorPairs?.[0]?.finish ?? null;
  const headerCompatibility = useMemo<FastenerCompatibilityFilter>(
    () => ({
      role: "header",
      lockItemId: firstLockItemId,
      secondLockItemId: secondLockItemId,
      headerB: values?.headerMember?.width,
      headerH: values?.headerMember?.height,
      headerOrientation: values?.headerMember?.orientation,
      lateralResistanceConfig: values?.lateralResistanceConfig,
      finish: structureFinish,
    }),
    [
      firstLockItemId,
      secondLockItemId,
      values?.headerMember?.width,
      values?.headerMember?.height,
      values?.headerMember?.orientation,
      values?.lateralResistanceConfig,
      structureFinish,
    ],
  );
  const joistCompatibility = useMemo<FastenerCompatibilityFilter>(
    () => ({
      role: "joist",
      lockItemId: firstLockItemId,
      secondLockItemId: secondLockItemId,
      alphaDeg: values?.joistMember?.alphaDeg,
      betaDeg: values?.joistMember?.betaDeg,
      finish: structureFinish,
    }),
    [firstLockItemId, secondLockItemId, values?.joistMember?.alphaDeg, values?.joistMember?.betaDeg, structureFinish],
  );
  // The inclined fastener applies the header rule plus its own stricter
  // length limit (isFastenerCompatibleWithInclined) — same geometry inputs
  // as the header rule, but its own "inclined" role so the backend applies
  // the extra constraint (see filterInclinedFasteners/buildCandidates).
  const inclinedCompatibility = useMemo<FastenerCompatibilityFilter>(
    () => ({
      role: "inclined",
      lockItemId: firstLockItemId,
      secondLockItemId: secondLockItemId,
      headerB: values?.headerMember?.width,
      headerH: values?.headerMember?.height,
      headerOrientation: values?.headerMember?.orientation,
      lateralResistanceConfig: values?.lateralResistanceConfig,
      finish: structureFinish,
    }),
    [
      firstLockItemId,
      secondLockItemId,
      values?.headerMember?.width,
      values?.headerMember?.height,
      values?.headerMember?.orientation,
      values?.lateralResistanceConfig,
      structureFinish,
    ],
  );
  const headerFastenersAborter = useRef(new AbortController());
  const headerFastenersQuery = useFasteners(
    { limit: 200, page: 1, q: "" },
    headerFastenersAborter.current.signal,
    isAdmin,
    headerCompatibility,
  );
  const headerFastenerOptions = headerFastenersQuery.data?.items ?? [];
  const isLoadingHeaderFasteners = headerFastenersQuery.isLoading;
  const joistFastenersAborter = useRef(new AbortController());
  const joistFastenersQuery = useFasteners(
    { limit: 200, page: 1, q: "" },
    joistFastenersAborter.current.signal,
    isAdmin,
    joistCompatibility,
  );
  const joistFastenerOptions = joistFastenersQuery.data?.items ?? [];
  const isLoadingJoistFasteners = joistFastenersQuery.isLoading;
  const inclinedFastenersAborter = useRef(new AbortController());
  const inclinedFastenersQuery = useFasteners(
    { limit: 200, page: 1, q: "" },
    inclinedFastenersAborter.current.signal,
    isAdmin,
    inclinedCompatibility,
  );
  const upliftFastenerOptions = inclinedFastenersQuery.data?.items ?? [];
  const isLoadingInclinedFasteners = inclinedFastenersQuery.isLoading;
  const isLoadingFasteners = isLoadingHeaderFasteners || isLoadingJoistFasteners || isLoadingInclinedFasteners;

  // A lock whose inclined counts are both 0 takes no inclined fasteners. Judged
  // only once every chosen lock is found in its list, and for the pair only
  // when none of the two takes them.
  const takesInclined = (lock: LockLight) => (lock.nInclinedUplift ?? 0) > 0 || (lock.nInclinedLateral ?? 0) > 0;
  const chosenFirstLock = firstLockItemId ? locksForConnectorOne.find((l) => l.id === firstLockItemId) : undefined;
  const chosenSecondLock = secondLockItemId ? locksForConnectorTwo.find((l) => l.id === secondLockItemId) : undefined;
  const inclinedNotAllowed =
    chosenFirstLock != null &&
    (secondLockItemId == null || chosenSecondLock != null) &&
    !takesInclined(chosenFirstLock) &&
    (chosenSecondLock == null || !takesInclined(chosenSecondLock));
  const chosenInclinedItemId = values?.fasteners?.inclinedItemId ?? null;
  const { reportCascade, automaticBlock, notices } = useNotices();
  const manualBlock = notices.find((notice) => notice.code === "manualGenerationInputsMissing");
  const generateFromDataRef = useNoticeTarget(actionTargetKey("generateFromData"));
  const generateManualRef = useNoticeTarget(actionTargetKey("generateManual"));
  useEffect(() => {
    if (inclinedNotAllowed && chosenInclinedItemId) {
      applyCascade(form, reportCascade, {
        code: "inclinedFastenerNotAllowed",
        causes: ["connectorPairs.0.lockItemId", "connectorPairs.1.lockItemId"],
        effect: "fasteners.inclinedItemId",
        next: null,
      });
    }
  }, [inclinedNotAllowed, chosenInclinedItemId, form, reportCascade]);

  const { connectorOne, noSecondLockAvailable } = useConnectorSelection({
    form,
    values: values as LockCalculatorFormValues,
    locks: locksForConnectorOne,
    locksForConnectorTwo,
    headerFastenerOptions,
    joistFastenerOptions,
    upliftFastenerOptions,
    isLoadingLocks: isLoadingLocksForConnectorOne,
    isLoadingLocksForConnectorTwo,
    isLoadingFasteners,
  });
  const fastenerLabel = (fastener: FastenerLight) => fastener.description;
  const connectorOneHasEvo = connectorOne?.evoAvailable ?? false;
  const isComplete = isConnectorsStepComplete(values as LockCalculatorFormValues);
  const FINISH_OPTIONS = useMemo<{ value: Finish; label: string; image?: string }[]>(
    () => [
      {
        value: FINISH_ALUMINIUM,
        label: t.lockCalculatorModule.finish.aluminium,
        image: `${LC_IMG}/aluminium-finish.png`,
      },
      { value: FINISH_EVO, label: t.lockCalculatorModule.finish.evo, image: `${LC_IMG}/evo-finish.png` },
    ],
    [t],
  );

  return (
    <>
      {/* ── Card 1: Connection Setup ── */}
      <Card
        style={{ minHeight: 400 }}
        accentColor="var(--accent-base, var(--red))"
        shadow
        className={`${styles.span12} ${styles.compactCard} ${styles.stepCard}`}
      >
        {stepper}
        <Card.Title>{t.lockCalculatorModule?.connector?.setupTitle}</Card.Title>
        <div className={styles.connectorRow}>
          <div className={styles.connectorHalfField}>
            <Controller
              name="connectorPairCount"
              control={form.control}
              render={({ field }) => (
                <FormField name="connectorPairCount"
                  label={t.lockCalculatorModule?.connector?.connectorPairCount}
                  error={form.formState.errors.connectorPairCount}
                  isOptional
                  showHelp={{
                    tooltip: t.lockCalculatorModule?.helpTexts?.connectorPairCount,
                    onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.connectorPairCount),
                  }}
                >
                  <div className={`${styles.imageSelectGrid} ${styles.imageSelectGrid2}`}>
                    {CONNECTOR_COUNT_OPTIONS.map((option) => {
                      const isSecondPairDisabled = option.value === 2 && noSecondLockAvailable;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(styles.imageSelectOption, {
                            [styles.imageSelectOptionSelected]: field.value === option.value,
                          })}
                          aria-pressed={field.value === option.value}
                          disabled={readOnly || isSecondPairDisabled}
                          title={
                            isSecondPairDisabled
                              ? t.lockCalculatorModule?.connector?.noCompatibleSecondConnector
                              : option.label
                          }
                          onClick={() => field.onChange(field.value === option.value ? null : option.value)}
                        >
                          <img
                            className={styles.imageSelectImage}
                            src={connectorCountImages[option.value]}
                            alt={option.label}
                          />
                          <span className={styles.imageSelectLabel}>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </FormField>
              )}
            />
          </div>
        </div>

        {/* No connector-pair count chosen yet (brand-new calculation) —
            nothing below is meaningful without it, so none of it renders. */}
        {values?.connectorPairCount != null && (
          <>
            {/* ── Card 2: Connector Selection ── */}
            <div className={styles.connectorRow}>
              <div className={styles.connectorHalfField}>
                <Controller
                  name="connectorPairs.0.lockItemId"
                  control={form.control}
                  render={({ field }) => (
                    <FormField name="connectorPairs.0.lockItemId"
                      label={t.lockCalculatorModule?.connection?.connectorOne}
                      error={form.formState.errors.connectorPairs?.[0]?.lockItemId}
                      showHelp={{
                        tooltip: t.lockCalculatorModule?.helpTexts?.connectorOneLock,
                        onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.connectorOneLock),
                      }}
                    >
                      {({ labelId, isInvalid }) => (
                        <Select<LockLight, false>
                          inputId={labelId}
                          value={locksForConnectorOne.find((s) => s.id === field.value) ?? null}
                          options={locksForConnectorOne}
                          getOptionValue={(opt) => opt.id}
                          getOptionLabel={lockLabel}
                          placeholder={t.lockCalculatorModule.selectPlaceholder}
                          isLoading={isLoadingLocksForConnectorOne}
                          isDisabled={readOnly}
                          isClearable
                          isInvalid={isInvalid}
                          onChange={(selected) => {
                            const nextValue = selected?.id ?? null;
                            field.onChange(nextValue);
                            form.setValue("connectorPairs.0.lockItemId", nextValue, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            });
                          }}
                        />
                      )}
                    </FormField>
                  )}
                />
                {!isLoadingLocksForConnectorOne &&
                  locksForConnectorOne.length === 0 &&
                  values?.headerMember?.width != null && (
                    <p className={styles.noCompatibleLockNote}>
                      {t.lockCalculatorModule.connector.noCompatibleConnector}
                    </p>
                  )}
              </div>
            </div>
            {values?.connectorPairCount === 2 && (
              <div className={styles.connectorRow}>
                <div className={styles.connectorHalfField}>
                  <Controller
                    name="connectorPairs.1.lockItemId"
                    control={form.control}
                    render={({ field }) => (
                      <FormField name="connectorPairs.1.lockItemId"
                        label={t.lockCalculatorModule?.connection?.connectorTwo}
                        error={form.formState.errors.connectorPairs?.[1]?.lockItemId}
                        showHelp={{
                          tooltip: t.lockCalculatorModule?.helpTexts?.connectorTwoLock,
                          onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.connectorTwoLock),
                        }}
                      >
                        {({ labelId }) => (
                          <Select<LockLight, false>
                            inputId={labelId}
                            value={locksForConnectorTwo.find((s) => s.id === field.value) ?? null}
                            options={locksForConnectorTwo}
                            getOptionValue={(opt) => opt.id}
                            getOptionLabel={lockLabel}
                            placeholder={t.lockCalculatorModule.selectPlaceholder}
                            isLoading={isLoadingLocksForConnectorTwo}
                            isDisabled={readOnly || values?.connectorPairs?.[0]?.lockItemId == null}
                            isClearable
                            onChange={(selected) => {
                              const nextValue = selected?.id ?? null;
                              field.onChange(nextValue);
                              form.setValue("connectorPairs.1.lockItemId", nextValue, {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              });
                            }}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </div>
              </div>
            )}

            <div className={styles.connectorRow}>
              <div className={styles.connectorHalfNarrow}>
                <Controller
                  name="connectorPairs.0.finish"
                  control={form.control}
                  render={({ field }) => (
                    <FormField name="connectorPairs.0.finish"
                      label={t.lockCalculatorModule?.connection?.finish}
                      error={form.formState.errors.connectorPairs?.[0]?.finish}
                      isOptional
                      showHelp={{
                        tooltip: t.lockCalculatorModule?.helpTexts?.connectorOneFinish,
                        onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.connectorOneFinish),
                      }}
                    >
                      <div className={`${styles.imageSelectGrid} ${styles.imageSelectGrid2}`}>
                        {FINISH_OPTIONS.map((option) => {
                          const isEvoDisabled = option.value === FINISH_EVO && (!connectorOne || !connectorOneHasEvo);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              className={classNames(styles.imageSelectOption, {
                                [styles.imageSelectOptionSelected]: field.value === option.value,
                              })}
                              aria-pressed={field.value === option.value}
                              disabled={readOnly || isEvoDisabled}
                              title={option.label}
                              onClick={() => field.onChange(field.value === option.value ? null : option.value)}
                            >
                              {option.image ? (
                                <img className={styles.imageSelectImage} src={option.image} alt={option.label} />
                              ) : (
                                <span style={finishBadgeStyle(option.value)}>
                                  {option.value === FINISH_ALUMINIUM
                                    ? t.lockCalculatorModule.finish.aluminiumShort
                                    : t.lockCalculatorModule.finish.evoShort}
                                </span>
                              )}
                              <span className={styles.imageSelectLabel}>{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </FormField>
                  )}
                />
              </div>
            </div>

            <div className={styles.connectorRow}>
              <div className={styles.connectorHalfField}>
                <Controller
                  name="lockstopItemId"
                  control={form.control}
                  render={({ field }) => (
                    <FormField name="lockstopItemId"
                      label={t.lockCalculatorModule?.fasteners?.lockstop}
                      error={form.formState.errors.lockstopItemId}
                      showHelp={{
                        tooltip: t.lockCalculatorModule?.helpTexts?.lockstop,
                        onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.lockstop),
                      }}
                    >
                      {({ labelId, isInvalid }) => (
                        <Select<LockStop, false>
                          inputId={labelId}
                          value={lockStops.find((s) => s.item.id === field.value) ?? null}
                          options={lockStops}
                          getOptionValue={(opt) => opt.item.id}
                          getOptionLabel={(s) => s.item.code}
                          placeholder={t.lockCalculatorModule.selectPlaceholder}
                          isLoading={isLoadingLockStops}
                          isDisabled={
                            readOnly ||
                            values?.connectorPairs?.[0]?.lockItemId == null ||
                            values?.lateralResistanceConfig !== LATERAL_RESISTANCE_CONFIG.LockStop
                          }
                          isClearable
                          isInvalid={isInvalid}
                          onChange={(selected) => field.onChange(selected?.item.id ?? null)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </div>
            </div>

            <div className={styles.connectorRow}>
              <div className={styles.connectorHalfField}>
                <Controller
                  name="fasteners.headerItemId"
                  control={form.control}
                  render={({ field }) => (
                    <FormField name="fasteners.headerItemId"
                      label={t.lockCalculatorModule?.fasteners?.headerFastener}
                      error={form.formState.errors.fasteners?.headerItemId}
                      showHelp={{
                        tooltip: t.lockCalculatorModule?.helpTexts?.headerFastener,
                        onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.headerFastener),
                      }}
                    >
                      {({ labelId, isInvalid }) => (
                        <Select<FastenerLight, false>
                          key={`${field.value ?? "__empty__"}-${headerFastenerOptions.length}`}
                          inputId={labelId}
                          value={
                            (field.value ? headerFastenerOptions.find((f) => f.id === field.value) : null) ?? null
                          }
                          options={headerFastenerOptions}
                          getOptionValue={(opt) => opt.id}
                          getOptionLabel={fastenerLabel}
                          placeholder={t.lockCalculatorModule.selectPlaceholder}
                          isLoading={isLoadingFasteners}
                          isDisabled={readOnly}
                          isClearable
                          isInvalid={isInvalid}
                          onChange={(selected) => {
                            field.onChange(selected?.id ?? null);
                            // Connectors step only: picking the header fastener
                            // selects the same fastener on the joist when it is
                            // a valid joist option. One-directional — the joist
                            // can then be changed freely, and clearing the
                            // header leaves the joist untouched.
                            if (selected && joistFastenerOptions.some((f) => f.id === selected.id)) {
                              form.setValue("fasteners.joistItemId", selected.id, { shouldDirty: true });
                            }
                          }}
                        />
                      )}
                    </FormField>
                  )}
                />
              </div>
            </div>

            <div className={styles.connectorRow}>
              <div className={styles.connectorHalfField}>
                <Controller
                  name="fasteners.joistItemId"
                  control={form.control}
                  render={({ field }) => (
                    <FormField name="fasteners.joistItemId"
                      label={t.lockCalculatorModule?.fasteners?.joistFastener}
                      error={form.formState.errors.fasteners?.joistItemId}
                      showHelp={{
                        tooltip: t.lockCalculatorModule?.helpTexts?.joistFastener,
                        onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.joistFastener),
                      }}
                    >
                      {({ labelId, isInvalid }) => (
                        <Select<FastenerLight, false>
                          key={`${field.value ?? "__empty__"}-${joistFastenerOptions.length}`}
                          inputId={labelId}
                          value={
                            (field.value ? joistFastenerOptions.find((f) => f.id === field.value) : null) ?? null
                          }
                          options={joistFastenerOptions}
                          getOptionValue={(opt) => opt.id}
                          getOptionLabel={fastenerLabel}
                          placeholder={t.lockCalculatorModule.selectPlaceholder}
                          isLoading={isLoadingFasteners}
                          isDisabled={
                            readOnly ||
                            values?.connectorPairs?.[0]?.lockItemId == null ||
                            (values?.connectorPairCount === 2 && values?.connectorPairs?.[1]?.lockItemId == null)
                          }
                          isClearable
                          isInvalid={isInvalid}
                          onChange={(selected) => field.onChange(selected?.id ?? null)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </div>
            </div>

            {/* Rendered at all only when an axis actually needs an inclined
            fastener (lateral=3 or upliftResistanceConfig=0 — both now
            load-gated upstream, see useLockCalculatorFormRules.ts) —
            hidden rather than merely disabled, since a greyed-out field
            for an axis with nothing to resist is confusing, not just
            unusable. */}
            {!inclinedNotAllowed &&
              (values?.lateralResistanceConfig === LATERAL_RESISTANCE_CONFIG.InclinedFasteners ||
                values?.upliftResistanceConfig === UPLIFT_RESISTANCE_CONFIG.InclinedFasteners) && (
              <div className={styles.connectorRow}>
                <div className={styles.connectorHalfField}>
                  <Controller
                    name="fasteners.inclinedItemId"
                    control={form.control}
                    render={({ field }) => (
                      <FormField name="fasteners.inclinedItemId"
                        label={t.lockCalculatorModule?.structural?.inclinedFastener}
                        error={form.formState.errors.fasteners?.inclinedItemId}
                        isOptional
                        showHelp={{
                          tooltip: t.lockCalculatorModule?.helpTexts?.upliftFastener,
                          onOpen: () => onOpenHelp(t.lockCalculatorModule?.helpTexts?.upliftFastener),
                        }}
                      >
                        {({ labelId, isInvalid }) => (
                          <Select<FastenerLight, false>
                            key={`${field.value ?? "__empty__"}-${upliftFastenerOptions.length}`}
                            inputId={labelId}
                            value={
                              (field.value ? upliftFastenerOptions.find((f) => f.id === field.value) : null) ?? null
                            }
                            options={upliftFastenerOptions}
                            getOptionValue={(opt) => opt.id}
                            getOptionLabel={fastenerLabel}
                            placeholder={t.lockCalculatorModule.selectPlaceholder}
                            isLoading={isLoadingFasteners}
                            isDisabled={readOnly}
                            isClearable
                            isInvalid={isInvalid}
                            onChange={(selected) => {
                              const nextValue = selected?.id ?? null;
                              field.onChange(nextValue);
                              form.setValue("fasteners.inclinedItemId", nextValue, {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              });
                            }}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div className={styles.wizardNav}>
          <button
            type="button"
            className={styles.wizardNavBtn}
            disabled={readOnly || isComplete || isTriggeringOperation || automaticBlock !== null}
            title={automaticBlock ? noticeText(automaticBlock, t) : undefined}
            ref={generateFromDataRef}
            onClick={() => generate("generateFromData")}
          >
            {t.lockCalculatorModule?.connector?.generateFromData}
          </button>
          <button
            type="button"
            className={styles.wizardNavBtn}
            disabled={readOnly || !isComplete || isTriggeringOperation}
            title={!isComplete && manualBlock ? noticeText(manualBlock, t) : undefined}
            ref={generateManualRef}
            onClick={() => generate("generateManual")}
          >
            {t.lockCalculatorModule?.connector?.generateManual}
          </button>
        </div>
      </Card>
    </>
  );
};
